import React, { useDeferredValue, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { BrowsePageFrame, BrowseResults, BrowseResultState } from '@/components/football';
import ReportFeedCards from '@/components/reports/ReportFeedCards';
import ReportFeedTable from '@/components/reports/ReportFeedTable';
import ReportsFilters from '@/components/reports/ReportsFilters';
import PageHeader from '@/components/layout/PageHeader';
import Pagination from '@/components/ui/Pagination';
import { reportKeys, reportsV2API } from '@/services/api/reportsV2';
import { mapReportsBrowse } from './ReportsPage/adapter';
import { defaultReportsURLState, parseReportsURLState, reportsStateToQuery, serializeReportsURLState } from './ReportsPage/urlState';
import { ReportCapability, ReportsURLState } from './ReportsPage/types';
import ReportsTabBar from './ReportsPage/ReportsTabBar';
import { useReportRealtime } from './reportRealtime';
import Button from '@/components/ui/Button';
import { downloadBlob } from '@/components/reports/downloadBlob';
import { useReportWorkspace } from '@/context/ReportWorkspaceContext';
import { useAuth } from '@/context/AuthContext';

const hasFilters = (state: ReportsURLState): boolean => Boolean(
  state.q || state.type || state.playerId || state.authorId || state.from || state.to
  || state.minRating !== null || state.maxRating !== null || state.medicalStatus
  || state.medicalSeverity || state.hasAttachment !== null,
);

const ReportsPageWorkspace: React.FC<{ workspaceId: string; showAuthorFilter: boolean }> = ({ workspaceId, showAuthorFilter }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const realtime = useReportRealtime(workspaceId);
  const state = parseReportsURLState(searchParams);
  const canonical = serializeReportsURLState(state);
  const canonicalSearch = canonical.toString();
  const rawSearch = searchParams.toString();
  const [searchValue, setSearchValue] = useState(state.q);
  const deferredSearch = useDeferredValue(searchValue);
  const urlRef = useRef(canonicalSearch);
  const suppressSearchRef = useRef(false);
  const exportErrorRef = useRef<HTMLParagraphElement>(null);
  const [exportState, setExportState] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const browseQuery = reportsStateToQuery(state);
  const query = useQuery({
    queryKey: reportKeys.browse(workspaceId, browseQuery),
    queryFn: async () => mapReportsBrowse(await reportsV2API.browse(workspaceId, browseQuery)),
    enabled: Boolean(workspaceId) && state.tab === 'reports',
  });

  useEffect(() => {
    if (rawSearch !== canonicalSearch) setSearchParams(canonical, { replace: true });
  }, [canonical, canonicalSearch, rawSearch, setSearchParams]);

  useLayoutEffect(() => {
    if (urlRef.current !== canonicalSearch) {
      suppressSearchRef.current = true;
      urlRef.current = canonicalSearch;
      setSearchValue(state.q);
    }
  }, [canonicalSearch, state.q]);

  useEffect(() => {
    if (deferredSearch === state.q) {
      suppressSearchRef.current = false;
      return;
    }
    if (suppressSearchRef.current) return;
    const timer = window.setTimeout(() => {
      setSearchParams(serializeReportsURLState({ ...state, q: deferredSearch, page: 1 }), { replace: true });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [deferredSearch, setSearchParams, state]);

  const commitState = (patch: Partial<ReportsURLState>, resetPage = true) => {
    setSearchParams(serializeReportsURLState({ ...state, q: searchValue, ...patch, page: resetPage ? 1 : patch.page ?? state.page }));
  };
  const clearFilters = () => {
    setSearchValue('');
    setSearchParams(serializeReportsURLState({ ...defaultReportsURLState, tab: state.tab }));
  };
  const returnTo = `/reports${canonicalSearch ? `?${canonicalSearch}` : ''}`;
  const detailHref = (type: string, id: string) => `/reports/${type}/${id}?${new URLSearchParams({ returnTo })}`;
  const data = query.data;
  const correctedPage = !data || query.isPlaceholderData
    ? undefined
    : data.meta.total === 0
      ? (state.page === 1 ? undefined : 1)
      : state.page > data.meta.totalPages ? Math.max(1, data.meta.totalPages) : undefined;

  useEffect(() => {
    if (correctedPage !== undefined) setSearchParams(serializeReportsURLState({ ...state, page: correctedPage }), { replace: true });
  }, [correctedPage, setSearchParams, state]);

  useEffect(() => {
    if (exportState === 'error') exportErrorRef.current?.focus();
  }, [exportState]);

  let results: BrowseResultState;
  if (state.tab === 'documents') {
    results = { status: 'empty', message: 'Documents are now managed as attachments from each report detail page.' };
  } else if (correctedPage !== undefined || (query.isPending && !data)) {
    results = { status: 'loading', message: 'Loading reports...' };
  } else if (query.isError && !data) {
    results = { status: 'error', message: 'Unable to load reports.', retry: () => { void query.refetch(); } };
  } else if (!data || data.meta.total === 0) {
    results = hasFilters(state)
      ? { status: 'filtered-empty', message: 'No reports match these filters.', clearFilters }
      : { status: 'empty', message: 'No reports have been created.' };
  } else {
    results = {
      status: 'success', refreshing: query.isFetching,
      table: <ReportFeedTable reports={data.data} detailHref={detailHref} />,
      cards: <ReportFeedCards reports={data.data} detailHref={detailHref} />,
    };
  }

  const createCapabilities = data?.capabilities.filter((capability: ReportCapability) => capability.startsWith('report.create.')) ?? [];
  const actions = createCapabilities.length > 0 || data?.capabilities.includes('report.export') ? <div className="flex flex-wrap gap-2">
    {createCapabilities.length > 0 && <details className="relative">
      <summary className="inline-flex min-h-11 cursor-pointer list-none items-center rounded-md bg-club-blue-700 px-4 text-sm font-semibold text-white hover:bg-club-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500">Create report</summary>
      <div className="absolute right-0 z-20 mt-2 min-w-52 rounded-lg bg-white p-2 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-700">
        {createCapabilities.map((capability: ReportCapability) => {
          const createType = capability.replace('report.create.', '');
          return <Link key={capability} to={`/reports/${createType}/new?${new URLSearchParams({ returnTo })}`} className="flex min-h-11 items-center rounded-md px-3 text-sm font-semibold capitalize text-gray-800 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:text-gray-100 dark:hover:bg-gray-800">{createType} report</Link>;
        })}
      </div>
    </details>}
    {data?.capabilities.includes('report.export') && <Button variant="outline" loading={exportState === 'pending'} loadingLabel="Exporting reports" onClick={() => {
      setExportState('pending');
      void reportsV2API.exportBrowse(workspaceId, browseQuery).then(blob => {
        downloadBlob(blob, 'reports.csv');
        setExportState('success');
      }).catch(() => setExportState('error'));
    }}>Export CSV</Button>}
  </div> : undefined;
  const total = data?.meta.total ?? 0;

  return (
    <BrowsePageFrame
      header={<><PageHeader title="Reports" subtitle="Review and manage staff reports across the active workspace" actions={state.tab === 'reports' ? actions : undefined} /><ReportsTabBar activeTab={state.tab} setActiveTab={tab => commitState({ tab }, false)} /></>}
      toolbar={state.tab === 'reports' ? <ReportsFilters state={state} searchValue={searchValue} showAuthorFilter={showAuthorFilter} facets={data?.facets} onSearch={value => { suppressSearchRef.current = false; setSearchValue(value); }} onChange={commitState} onClear={clearFilters} /> : <div />}
      resultSummary={<><p aria-live="polite">{state.tab === 'reports' ? `${total} ${total === 1 ? 'report' : 'reports'}${hasFilters(state) ? ' matching filters' : ''}` : 'Report attachments'}</p>{exportState === 'success' && <p role="status">Report export downloaded.</p>}{exportState === 'error' && <p ref={exportErrorRef} role="alert" tabIndex={-1} className="text-red-700 dark:text-red-300">Unable to export reports. Review the filters or try again.</p>}{realtime.status === 'disconnected' && <p role="status">Live updates disconnected. Reports can still be refreshed.</p>}</>}
      results={<BrowseResults state={results} />}
      pagination={state.tab === 'reports' && correctedPage === undefined && total > 0 ? <Pagination currentPage={state.page} totalCount={total} pageSize={state.pageSize} onPageChange={page => commitState({ page }, false)} onPageSizeChange={pageSize => commitState({ pageSize: pageSize as ReportsURLState['pageSize'] })} isLoading={query.isFetching} /> : undefined}
    />
  );
};

const ReportsPage: React.FC = () => {
  const { user } = useAuth();
  const workspaceId = useReportWorkspace();
  if (!workspaceId) return <p role="status">Select a workspace to view reports.</p>;
  return <ReportsPageWorkspace key={workspaceId} workspaceId={workspaceId} showAuthorFilter={user?.role !== 'scout'} />;
};

export default ReportsPage;
